import { useState } from 'react'
import { Hammer, Loader2, CheckCircle, XCircle } from 'lucide-react'
import { buildTool, type SuggestedAction, type BuildToolResponse } from '@/api/plugins'

interface BuildToolButtonProps {
  action: SuggestedAction
  onComplete?: (response: BuildToolResponse) => void
}

/**
 * Button component for building missing tools via Claude Code.
 *
 * When clicked, this sends a request to the backend which forwards it
 * to the Claude Code MCP server to implement the missing tool.
 */
export function BuildToolButton({ action, onComplete }: BuildToolButtonProps) {
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<BuildToolResponse | null>(null)

  const handleClick = async () => {
    if (loading) return

    setLoading(true)
    setResult(null)

    try {
      const response = await buildTool(action)
      setResult(response)
      onComplete?.(response)
    } catch (error) {
      const errorResponse: BuildToolResponse = {
        ok: false,
        message: 'Failed to build tool',
        error: error instanceof Error ? error.message : 'Unknown error',
      }
      setResult(errorResponse)
      onComplete?.(errorResponse)
    } finally {
      setLoading(false)
    }
  }

  // Show result state if we have one
  if (result) {
    return (
      <div
        className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
          result.ok
            ? 'bg-green-500/20 text-green-400 border border-green-500/30'
            : 'bg-red-500/20 text-red-400 border border-red-500/30'
        }`}
      >
        {result.ok ? (
          <>
            <CheckCircle className="w-4 h-4" />
            <span>Tool built successfully</span>
          </>
        ) : (
          <>
            <XCircle className="w-4 h-4" />
            <span>{result.error || result.message}</span>
          </>
        )}
      </div>
    )
  }

  return (
    <button
      onClick={handleClick}
      disabled={loading}
      className={`
        inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm
        transition-all duration-200
        ${
          loading
            ? 'bg-accent/50 cursor-wait'
            : 'bg-accent hover:bg-accent-hover active:scale-95'
        }
        text-white font-medium
        border border-accent-hover/30
        shadow-sm hover:shadow-md
      `}
      title={action.description}
    >
      {loading ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Building...</span>
        </>
      ) : (
        <>
          <Hammer className="w-4 h-4" />
          <span>{action.label}</span>
        </>
      )}
    </button>
  )
}
